import React, { useState } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { filestackClient } from '../../utils/filestack';
import { toast } from 'react-toastify';

/**
 * A reusable component for uploading images using Filestack
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onUploadSuccess - Callback function that receives the uploaded file URL
 * @param {Function} props.onUploadError - Optional callback function for handling errors
 * @param {string} props.buttonText - Optional custom button text
 * @param {Object} props.buttonProps - Optional additional props for the Button component
 */
const ImageUploader = ({ 
  onUploadSuccess, 
  onUploadError,
  buttonText,
  buttonProps = {}
}) => {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    try {
      setLoading(true);
      
      // Use the same approach as in ProfileSection
      await filestackClient.picker({
        accept: ['image/*'],
        maxFiles: 1,
        uploadInBackground: false,
        onUploadDone: (res) => {
          if (res.filesUploaded && res.filesUploaded.length > 0) {
            const uploadedFileUrl = res.filesUploaded[0].url;
            console.log('Uploaded file URL:', uploadedFileUrl);
            onUploadSuccess(uploadedFileUrl);
            toast.success(t('upload.success'));
          } else {
            handleError(new Error('No files were uploaded'));
          }
        }
      }).open();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error) => {
    console.error('Error uploading image:', error);
    toast.error(t('upload.error'));
    if (onUploadError) {
      onUploadError(error);
    }
  };

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={loading ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
        onClick={handleUpload}
        disabled={loading}
        {...buttonProps}
      >
        {loading ? t('upload.uploading') : buttonText || t('upload.image')}
      </Button>
    </Box>
  );
};

export default ImageUploader; 